import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Chip,
  CircularProgress,
  Pagination as MuiPagination,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ListPageLayout from '../components/shared/list-page-layout';
import ListFilters, { FilterField } from '../components/shared/list-filters';
import { TableSkeleton } from '../components/shared/skeletons';
import { IListPublicAccountRequest } from '../../model/base/request/list-public-account-request';
import { usePublicAccountsController } from './context/public-accounts-controller';
import { usePublicAccountsState } from './context/public-accounts-context';

export function PublicAccountsComponent() {
  const { loadData, loadCategories } = usePublicAccountsController();
  const { publicAccounts, categories, loading, loadingFilters } = usePublicAccountsState();
  const [currentPage, setCurrentPage] = useState(1);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filters, setFilters] = useState<IListPublicAccountRequest>({
    page: 0,
    pageSize: 12,
    term: '',
    categoryId: undefined,
    subcategoryId: undefined,
    year: undefined,
    startDate: '',
    endDate: '',
  });

  // Carregar categorias ao montar o componente
  useEffect(() => {
    loadCategories();
    loadData(filters);
  }, []);

  // Opções de filtro dinâmicas - Categorias
  const categoryOptions = categories.map(cat => ({
    value: cat.id,
    label: `${cat.name} (${cat.publicationCount})`,
  }));

  // Opções de filtro dinâmicas - Subcategorias (baseadas na categoria selecionada)
  const selectedCategory = categories.find(cat => cat.id === filters.categoryId);
  const subcategoryOptions = selectedCategory?.subcategories.map(subcat => ({
    value: subcat.id,
    label: `${subcat.name} (${subcat.publicationCount})`,
  })) || [];

  // Opções de ano (últimos 10 anos)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => ({
    value: currentYear - i,
    label: (currentYear - i).toString(),
  }));

  const filterFields: FilterField[] = [
    {
      name: 'term',
      label: 'Pesquisar',
      type: 'text',
      placeholder: 'Buscar por descrição',
      fullWidth: false,
    },
    {
      name: 'categoryId',
      label: 'Categoria',
      type: 'select',
      options: categoryOptions,
      placeholder: 'Selecione uma categoria',
    },
    {
      name: 'subcategoryId',
      label: 'Subcategoria',
      type: 'select',
      options: subcategoryOptions,
      placeholder: 'Selecione uma subcategoria',
      disabled: !filters.categoryId || subcategoryOptions.length === 0,
    },
    {
      name: 'year',
      label: 'Ano',
      type: 'select',
      options: yearOptions,
      placeholder: 'Selecione um ano',
    },
    {
      name: 'startDate',
      label: 'Data Inicial',
      type: 'date',
    },
    {
      name: 'endDate',
      label: 'Data Final',
      type: 'date',
    },
  ];

  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [name]: value,
      };

      // Se a categoria mudou, limpar subcategoria
      if (name === 'categoryId') {
        newFilters.subcategoryId = undefined;
      }

      return newFilters;
    });
  };

  const handleSearch = () => {
    const searchFilters = { ...filters, page: 0 };
    setCurrentPage(1);
    setFilters(searchFilters);
    loadData(searchFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: IListPublicAccountRequest = {
      page: 0,
      pageSize: 12,
      term: '',
      categoryId: undefined,
      subcategoryId: undefined,
      year: undefined,
      startDate: '',
      endDate: '',
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    loadData(clearedFilters);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    const newFilters = { ...filters, page: page - 1 };
    setFilters(newFilters);
    loadData(newFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleViewDocument = (url?: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleDownloadDocument = (url?: string, description?: string) => {
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = `conta-publica-${description || 'documento'}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <ListPageLayout
      title="Contas Públicas"
      subtitle="Consulte as contas públicas do município"
      breadcrumbs={[
        { label: 'Contas Públicas' },
      ]}
      filters={
        <ListFilters
          fields={filterFields.map((field) => ({
            ...field,
            value: filters[field.name as keyof IListPublicAccountRequest],
          }))}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={loading || loadingFilters}
        />
      }
    >
      {loading && !publicAccounts.hasItems() ? (
        <TableSkeleton rows={12} columns={5} hasActions={true} />
      ) : !publicAccounts.hasItems() ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nenhuma conta pública encontrada com os filtros aplicados.
        </Alert>
      ) : (
        <>
          <Box sx={{ position: 'relative' }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1000,
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={60} />
              </Box>
            )}
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {publicAccounts.items.map((account) => (
                  <Paper key={account.id} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        backgroundColor: 'primary.main',
                        px: 2,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <PictureAsPdfIcon sx={{ fontSize: 24, color: 'error.light' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'white', flex: 1 }}>
                        {formatDate(account.date)}
                      </Typography>
                      {account.category && (
                        <Chip
                          label={account.category}
                          size="small"
                          sx={{ backgroundColor: 'white', color: 'primary.main', fontWeight: 600, maxWidth: 120 }}
                        />
                      )}
                    </Box>
                    <Box sx={{ px: 2, py: 1.5 }}>
                      {account.subcategory && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                            Subcategoria
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {account.subcategory}
                          </Typography>
                        </>
                      )}
                      {account.description && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            {account.description}
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      {account.documents && account.documents.length > 0 ? (
                        <>
                          <Tooltip title="Visualizar PDF" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDocument(account.documents?.[0]?.fileViewUrl)}
                              disabled={!account.documents?.[0]?.fileViewUrl}
                              sx={{ '&:hover': { backgroundColor: 'primary.main', color: 'white' } }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Baixar PDF" arrow>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleDownloadDocument(account.documents?.[0]?.fileDownloadUrl, account.description)}
                              disabled={!account.documents?.[0]?.fileDownloadUrl}
                              sx={{ '&:hover': { backgroundColor: 'success.main', color: 'white' } }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          Sem documentos
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
            <TableContainer
              component={Paper}
              elevation={3}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                opacity: loading ? 0.5 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              <Table sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: 'primary.main',
                    }}
                  >
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '60px',
                      }}
                    >
                      <AccountBalanceIcon sx={{ verticalAlign: 'middle' }} />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '15%',
                      }}
                    >
                      Data
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                      }}
                    >
                      Descrição
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '15%',
                      }}
                    >
                      Categoria
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '15%',
                      }}
                    >
                      Subcategoria
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '150px',
                        textAlign: 'center',
                      }}
                    >
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {publicAccounts.items.map((account) => (
                    <TableRow
                      key={account.id}
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: 'action.hover',
                        },
                        '&:hover': {
                          backgroundColor: 'action.selected',
                        },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <PictureAsPdfIcon
                            sx={{
                              fontSize: 32,
                              color: 'error.main',
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(account.date)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 0, overflow: 'hidden' }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.5,
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {account.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={account.category || '-'}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {account.subcategory || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'center',
                          }}
                        >
                          {account.documents && account.documents.length > 0 && (
                            <>
                              <Tooltip title="Visualizar PDF" arrow>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewDocument(account.documents?.[0]?.fileViewUrl)}
                                  disabled={!account.documents?.[0]?.fileViewUrl}
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: 'primary.main',
                                      color: 'white',
                                    },
                                  }}
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Baixar PDF" arrow>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleDownloadDocument(account.documents?.[0]?.fileDownloadUrl, account.description)}
                                  disabled={!account.documents?.[0]?.fileDownloadUrl}
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: 'success.main',
                                      color: 'white',
                                    },
                                  }}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {(!account.documents || account.documents.length === 0) && (
                            <Typography variant="caption" color="text.disabled">
                              Sem documentos
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )}
          </Box>

          {publicAccounts.pageCount > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
                mb: 2,
              }}
            >
              <MuiPagination
                count={publicAccounts.pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                disabled={loading}
              />
            </Box>
          )}
        </>
      )}
    </ListPageLayout>
  );
}
